// AI Helper Utilities for Earn-E Chore Helper
// Smart suggestions powered by Cloud Functions + local fallbacks
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { app, db } from '../firebase';
import { searchRecipes, getRecipeDetails, formatRecipeForApp } from './recipeAPI';

const functions = getFunctions(app, 'us-central1');

// Age-appropriate chore suggestions
const choreSuggestions = {
  '5-7': [
    { title: 'Make Your Bed', description: 'Make your bed neatly every morning', allowance: 1, photoRequired: 'after' },
    { title: 'Put Away Toys', description: 'Clean up and organize your toys', allowance: 1, photoRequired: 'after' },
    { title: 'Feed the Pet', description: 'Give food and water to family pets', allowance: 2, photoRequired: 'none' },
    { title: 'Water Plants', description: 'Water the indoor plants', allowance: 1.5, photoRequired: 'after' },
    { title: 'Set the Table', description: 'Set the table for dinner', allowance: 1, photoRequired: 'after' }
  ],
  '8-10': [
    { title: 'Do the Dishes', description: 'Wash and dry the dishes after meals', allowance: 3, photoRequired: 'after' },
    { title: 'Take Out Trash', description: 'Empty trash cans and take to curb', allowance: 2, photoRequired: 'none' },
    { title: 'Vacuum Room', description: 'Vacuum your room and common areas', allowance: 3, photoRequired: 'both' },
    { title: 'Fold Laundry', description: 'Fold and put away clean laundry', allowance: 3, photoRequired: 'after' },
    { title: 'Clean Bathroom', description: 'Clean sink, counter, and mirror', allowance: 4, photoRequired: 'both' },
    { title: 'Sweep Floors', description: 'Sweep kitchen and dining room floors', allowance: 2.5, photoRequired: 'after' }
  ],
  '11-13': [
    { title: 'Mow the Lawn', description: 'Mow the front and back yard', allowance: 10, photoRequired: 'both' },
    { title: 'Do Laundry', description: 'Wash, dry, fold, and put away laundry', allowance: 5, photoRequired: 'after' },
    { title: 'Cook a Meal', description: 'Help prepare a family meal', allowance: 5, photoRequired: 'after' },
    { title: 'Deep Clean Kitchen', description: 'Clean counters, appliances, and floors', allowance: 6, photoRequired: 'both' },
    { title: 'Organize Garage', description: 'Organize and clean the garage', allowance: 8, photoRequired: 'both' },
    { title: 'Wash Car', description: 'Wash and vacuum the family car', allowance: 7, photoRequired: 'both' }
  ],
  '14+': [
    { title: 'Grocery Shopping', description: 'Help with weekly grocery shopping', allowance: 10, photoRequired: 'none' },
    { title: 'Yard Work', description: 'Complete yard work including weeding and trimming', allowance: 15, photoRequired: 'both' },
    { title: 'Babysit Siblings', description: 'Watch younger siblings for 2 hours', allowance: 12, photoRequired: 'none' },
    { title: 'Deep Clean House', description: 'Complete deep cleaning of entire house', allowance: 20, photoRequired: 'both' },
    { title: 'Meal Prep', description: 'Prepare meals for the week', allowance: 15, photoRequired: 'after' },
    { title: 'Home Repairs', description: 'Help with basic home maintenance tasks', allowance: 10, photoRequired: 'both' }
  ]
};

// Shopping categories
const shoppingCategories = {
  'Produce': ['apple', 'banana', 'orange', 'carrot', 'lettuce', 'tomato', 'potato', 'onion', 'fruit', 'vegetable', 'salad'],
  'Dairy': ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'eggs'],
  'Meat & Seafood': ['chicken', 'beef', 'pork', 'fish', 'salmon', 'shrimp', 'turkey', 'bacon', 'meat'],
  'Bakery': ['bread', 'bagel', 'muffin', 'cake', 'cookie', 'pastry', 'donut'],
  'Pantry': ['pasta', 'rice', 'cereal', 'flour', 'sugar', 'oil', 'sauce', 'soup', 'beans', 'canned'],
  'Snacks': ['chips', 'crackers', 'popcorn', 'nuts', 'candy', 'chocolate', 'pretzels', 'granola'],
  'Beverages': ['juice', 'soda', 'water', 'coffee', 'tea', 'sports drink', 'lemonade'],
  'Frozen': ['ice cream', 'frozen pizza', 'frozen vegetable', 'popsicle', 'frozen meal'],
  'Personal Care': ['shampoo', 'soap', 'toothpaste', 'deodorant', 'lotion', 'tissue'],
  'Household': ['paper towel', 'toilet paper', 'cleaner', 'detergent', 'dish soap', 'trash bag'],
  'Toys & Games': ['toy', 'game', 'puzzle', 'lego', 'doll', 'action figure', 'board game'],
  'Books & Media': ['book', 'magazine', 'movie', 'video game', 'dvd', 'blu-ray'],
  'School Supplies': ['notebook', 'pen', 'pencil', 'marker', 'backpack', 'binder', 'folder'],
  'Electronics': ['phone', 'tablet', 'computer', 'headphone', 'charger', 'cable'],
  'Clothing': ['shirt', 'pants', 'shoes', 'socks', 'jacket', 'dress', 'shorts'],
  'Other': []
};

/**
 * Suggest chores based on child's age (Cloud Function with fallback)
 * @param {number} age - Child's age
 * @param {number} count - Number of suggestions to return
 * @param {Array} currentChores - Currently assigned chores (optional)
 * @returns {Promise<Array>} Array of chore suggestions
 */
export async function suggestChores(age, count = 5, currentChores = []) {
  try {
    const suggestChoresFunc = httpsCallable(functions, 'suggestChores');
    const result = await suggestChoresFunc({ age, count, currentChores });
    return result.data.suggestions || suggestChoresLocal(age, count);
  } catch (error) {
    console.log('Using local chore suggestions (Cloud Function unavailable)');
    return suggestChoresLocal(age, count);
  }
}

/**
 * Local fallback for chore suggestions
 */
function suggestChoresLocal(age, count = 5) {
  let ageGroup;
  
  if (age <= 7) {
    ageGroup = '5-7';
  } else if (age <= 10) {
    ageGroup = '8-10';
  } else if (age <= 13) {
    ageGroup = '11-13';
  } else {
    ageGroup = '14+';
  }
  
  const chores = [...choreSuggestions[ageGroup]];
  
  // Shuffle and return requested count
  const shuffled = chores.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Categorize a shopping item based on its name
 * @param {string} itemName - Name of the shopping item
 * @returns {string} Category name
 */
export function categorizeShoppingItem(itemName) {
  const lowerName = itemName.toLowerCase();
  
  for (const [category, keywords] of Object.entries(shoppingCategories)) {
    for (const keyword of keywords) {
      if (lowerName.includes(keyword)) {
        return category;
      }
    }
  }
  
  return 'Other';
}

/**
 * Generate motivational messages for kids
 * @param {string} context - Context for the message (task_complete, streak, allowance_goal)
 * @param {object} data - Additional data for personalization
 * @returns {string} Motivational message
 */
export function generateMotivationalMessage(context, data = {}) {
  const messages = {
    task_complete: [
      '🌟 Awesome job! Keep up the great work!',
      '🎉 You crushed it! Your parents will be proud!',
      '⭐ Way to go! You are on fire today!',
      '🚀 Amazing work! You are a superstar!',
      '💪 Fantastic! You are getting stronger every day!'
    ],
    streak: [
      `🔥 ${data.days}-day streak! You are unstoppable!`,
      `⚡ ${data.days} days in a row! You are a champion!`,
      `🏆 ${data.days}-day streak! Keep the momentum going!`,
      `🌈 ${data.days} days straight! You are incredible!`,
      `💫 ${data.days}-day streak! Nothing can stop you!`
    ],
    allowance_goal: [
      `💰 You are ${data.percentage}% to your goal! Almost there!`,
      `🎯 ${data.percentage}% complete! Keep saving!`,
      `💵 ${data.percentage}% of your goal reached! So close!`,
      `🌟 ${data.percentage}% there! You are doing great!`,
      `📈 ${data.percentage}% progress! Keep it up!`
    ],
    welcome: [
      '👋 Welcome back! Ready to accomplish something great today?',
      '🌞 Hey there! Let us make today awesome!',
      '🎈 Welcome! Your tasks are waiting for you!',
      '🌟 Hello! Time to shine and earn some rewards!',
      '⭐ Welcome back! Let us get things done!'
    ]
  };
  
  const messageArray = messages[context] || messages.welcome;
  return messageArray[Math.floor(Math.random() * messageArray.length)];
}

/**
 * Calculate achievement badges earned
 * @param {object} userStats - User statistics
 * @returns {Array} Array of earned badges
 */
export function calculateBadges(userStats) {
  const badges = [];
  
  if (userStats.totalEarned >= 50) {
    badges.push({ name: 'Money Maker', icon: '💰', description: 'Earned $50 total' });
  }
  if (userStats.totalEarned >= 100) {
    badges.push({ name: 'Century Club', icon: '💯', description: 'Earned $100 total' });
  }
  if (userStats.streak >= 7) {
    badges.push({ name: 'Week Warrior', icon: '🔥', description: '7-day streak' });
  }
  if (userStats.streak >= 30) {
    badges.push({ name: 'Month Master', icon: '🏆', description: '30-day streak' });
  }
  if (userStats.tasksCompleted >= 10) {
    badges.push({ name: 'Task Tackler', icon: '⭐', description: '10 tasks completed' });
  }
  if (userStats.tasksCompleted >= 50) {
    badges.push({ name: 'Super Star', icon: '🌟', description: '50 tasks completed' });
  }
  if (userStats.tasksCompleted >= 100) {
    badges.push({ name: 'Century Champion', icon: '🏅', description: '100 tasks completed' });
  }
  
  return badges;
}

/**
 * Estimate time to complete a task based on description
 * @param {string} taskTitle - Title of the task
 * @returns {number} Estimated minutes to complete
 */
export function estimateTaskTime(taskTitle) {
  const lowerTitle = taskTitle.toLowerCase();
  
  const timeEstimates = {
    'bed': 5,
    'dishes': 15,
    'vacuum': 20,
    'laundry': 30,
    'mow': 45,
    'clean': 20,
    'organize': 30,
    'cook': 40,
    'wash car': 30,
    'trash': 5,
    'pet': 10,
    'plants': 10
  };
  
  for (const [keyword, minutes] of Object.entries(timeEstimates)) {
    if (lowerTitle.includes(keyword)) {
      return minutes;
    }
  }
  
  return 15; // Default estimate
}

// ===== AI LEARNING SYSTEM =====

/**
 * Save a conversation message to Firestore for AI learning
 * @param {string} userId - User's UID
 * @param {string} assistantType - 'earne' or 'burt'
 * @param {string} messageType - 'user' or 'assistant'
 * @param {string} content - Message content
 * @param {object} context - Additional context (tasks, preferences, etc.)
 */
export async function saveConversationMessage(userId, assistantType, messageType, content, context = {}) {
  if (!userId) return;
  // child: prefix means no direct Firebase Auth — this should not happen if anonymousUid was set
  // but guard anyway to prevent permission errors on accounts that haven't re-logged in yet
  if (userId.startsWith('child:')) return;
  try {
    const conversationRef = collection(db, 'aiLearning', userId, 'conversations');
    await addDoc(conversationRef, {
      assistantType,
      messageType,
      content,
      context,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error saving conversation message:', error);
  }
}

/**
 * Get conversation history for a user and assistant
 * @param {string} userId - User's UID
 * @param {string} assistantType - 'earne' or 'burt'
 * @param {number} limitCount - Number of recent messages to retrieve
 * @returns {Array} Array of conversation messages
 */
export async function getConversationHistory(userId, assistantType, limitCount = 50) {
  if (!userId || userId.startsWith('child:')) return []; // child accounts: no cloud history yet
  try {
    const conversationRef = collection(db, 'aiLearning', userId, 'conversations');
    const q = query(
      conversationRef,
      where('assistantType', '==', assistantType),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return messages.reverse(); // Return in chronological order
  } catch (error) {
    console.error('Error getting conversation history:', error);
    return [];
  }
}

/**
 * Get or create AI profile for a user
 * @param {string} userId - User's UID
 * @param {string} assistantType - 'earne' or 'burt'
 * @returns {object} AI profile data
 */
export async function getAIProfile(userId, assistantType) {
  if (!userId || userId.startsWith('child:')) return createDefaultProfile(assistantType);
  try {
    const profileRef = doc(db, 'aiLearning', userId, 'profiles', assistantType);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      return profileSnap.data();
    } else {
      // Create default profile
      const defaultProfile = createDefaultProfile(assistantType);
      await setDoc(profileRef, defaultProfile);
      return defaultProfile;
    }
  } catch (error) {
    console.error('Error getting AI profile:', error);
    return createDefaultProfile(assistantType);
  }
}

/**
 * Update AI profile with new learning data
 * @param {string} userId - User's UID
 * @param {string} assistantType - 'earne' or 'burt'
 * @param {object} updates - Profile updates
 */
export async function updateAIProfile(userId, assistantType, updates) {
  // Child accounts (uid: 'child:...') have no Firebase Auth — Firestore rules block writes
  // to aiLearning collection. Skip silently; AI still works, just no personalization writes.
  if (!userId || userId.startsWith('child:')) return;
  try {
    const profileRef = doc(db, 'aiLearning', userId, 'profiles', assistantType);
    await updateDoc(profileRef, {
      ...updates,
      lastUpdated: serverTimestamp(),
      lastUpdatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating AI profile:', error);
  }
}

/**
 * Analyze conversation patterns to learn user preferences
 * @param {Array} conversationHistory - Array of conversation messages
 * @param {string} assistantType - 'earne' or 'burt'
 * @returns {object} Learned preferences and patterns
 */
export function analyzeConversationPatterns(conversationHistory, assistantType) {
  const analysis = {
    preferredTopics: {},
    commonQuestions: {},
    responsePatterns: {},
    userBehavior: {},
    lastInteraction: null
  };

  if (conversationHistory.length === 0) return analysis;

  // Analyze user messages
  const userMessages = conversationHistory.filter(msg => msg.messageType === 'user');

  // Track preferred topics
  userMessages.forEach(msg => {
    const content = msg.content.toLowerCase();
    
    // Topic analysis
    if (content.includes('task') || content.includes('chore')) {
      analysis.preferredTopics.tasks = (analysis.preferredTopics.tasks || 0) + 1;
    }
    if (content.includes('money') || content.includes('earn') || content.includes('allowance')) {
      analysis.preferredTopics.money = (analysis.preferredTopics.money || 0) + 1;
    }
    if (content.includes('help') || content.includes('tip')) {
      analysis.preferredTopics.help = (analysis.preferredTopics.help || 0) + 1;
    }
    if (content.includes('shopping') || content.includes('buy') || content.includes('list')) {
      analysis.preferredTopics.shopping = (analysis.preferredTopics.shopping || 0) + 1;
    }
    if (content.includes('analytics') || content.includes('stats') || content.includes('insight')) {
      analysis.preferredTopics.analytics = (analysis.preferredTopics.analytics || 1) + 1;
    }
  });

  // Track common questions
  userMessages.forEach(msg => {
    const content = msg.content.toLowerCase();
    if (content.includes('?')) {
      // Extract question patterns
      if (content.includes('what') || content.includes('how') || content.includes('when') || content.includes('why')) {
        analysis.commonQuestions.information = (analysis.commonQuestions.information || 0) + 1;
      }
    }
  });

  // User behavior patterns
  analysis.userBehavior.messageFrequency = userMessages.length;
  analysis.userBehavior.avgMessageLength = userMessages.reduce((sum, msg) => sum + msg.content.length, 0) / userMessages.length || 0;
  analysis.userBehavior.interactionTimes = userMessages.map(msg => new Date(msg.createdAt).getHours());
  
  // Find most active hour
  const hourCounts = analysis.userBehavior.interactionTimes.reduce((acc, hour) => {
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {});
  analysis.userBehavior.mostActiveHour = Object.keys(hourCounts).reduce((a, b) => hourCounts[a] > hourCounts[b] ? a : b, '12');

  analysis.lastInteraction = conversationHistory[conversationHistory.length - 1]?.createdAt;

  return analysis;
}

/**
 * Generate proactive suggestions based on learned patterns
 * @param {object} aiProfile - User's AI profile
 * @param {object} currentContext - Current app context (tasks, family data, etc.)
 * @param {string} assistantType - 'earne' or 'burt'
 * @returns {Array} Array of proactive suggestions
 */
export function generateProactiveSuggestions(aiProfile, currentContext, assistantType) {
  const suggestions = [];
  const patterns = aiProfile.learnedPatterns || {};
  const currentHour = new Date().getHours();

  if (assistantType === 'earne') {
    // Child-focused proactive suggestions
    const pendingTasks = currentContext.tasks?.filter(task => task.status === 'pending') || [];
    const completedTasks = currentContext.completedTasks || [];
    
    // Suggest based on time of day
    if (currentHour >= 7 && currentHour <= 9) {
      suggestions.push({
        type: 'morning_motivation',
        content: "Good morning! Ready to tackle your tasks today? 🌅",
        priority: 'high'
      });
    } else if (currentHour >= 15 && currentHour <= 17) {
      suggestions.push({
        type: 'afternoon_check',
        content: "How are your tasks going? Need any help? 🎯",
        priority: 'medium'
      });
    }

    // Suggest based on task patterns
    if (pendingTasks.length > 0 && patterns.preferredTopics?.tasks > 2) {
      suggestions.push({
        type: 'task_reminder',
        content: `You have ${pendingTasks.length} task${pendingTasks.length > 1 ? 's' : ''} to complete! Let's get started! 💪`,
        priority: 'high'
      });
    }

    // Suggest based on money interest
    if (patterns.preferredTopics?.money > 1) {
      const totalEarned = completedTasks.reduce((sum, task) => sum + (task.allowance || 0), 0);
      suggestions.push({
        type: 'earnings_update',
        content: `You've earned $${totalEarned.toFixed(2)} so far! Keep up the great work! 💰`,
        priority: 'medium'
      });
    }

  } else if (assistantType === 'burt') {
    // Parent-focused proactive suggestions
    const shoppingList = currentContext.shoppingList || [];
    const tasks = currentContext.tasks || [];

    // Suggest based on analytics interest
    if (patterns.preferredTopics?.analytics > 1) {
      const completedTasks = tasks.filter(task => task.status === 'completed');
      const completionRate = tasks.length > 0 ? Math.round((completedTasks.length / tasks.length) * 100) : 0;
      
      suggestions.push({
        type: 'analytics_update',
        content: `Family task completion rate: ${completionRate}%. ${completionRate > 80 ? 'Excellent!' : completionRate > 60 ? 'Good progress!' : 'Room for improvement!'}`,
        priority: 'medium'
      });
    }

    // Suggest based on shopping patterns
    if (patterns.preferredTopics?.shopping > 1 && shoppingList.length > 0) {
      const pendingItems = shoppingList.filter(item => !item.approved);
      if (pendingItems.length > 0) {
        suggestions.push({
          type: 'shopping_reminder',
          content: `You have ${pendingItems.length} shopping item${pendingItems.length > 1 ? 's' : ''} waiting for approval.`,
          priority: 'medium'
        });
      }
    }

    // Suggest based on family management patterns
    if (patterns.preferredTopics?.help > 1) {
      suggestions.push({
        type: 'management_tip',
        content: "Would you like some tips on improving family task management? 💡",
        priority: 'low'
      });
    }
  }

  return suggestions.sort((a, b) => {
    const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
}

/**
 * Create default AI profile
 * @param {string} assistantType - 'earne' or 'burt'
 * @returns {object} Default profile data
 */
function createDefaultProfile(assistantType) {
  const baseProfile = {
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    learnedPatterns: {
      preferredTopics: {},
      commonQuestions: {},
      responsePatterns: {},
      userBehavior: {
        messageFrequency: 0,
        avgMessageLength: 0,
        mostActiveHour: '12',
        interactionTimes: []
      }
    },
    proactiveSuggestions: [],
    customization: {
      preferredLanguage: 'en',
      responseStyle: assistantType === 'earne' ? 'encouraging' : 'analytical',
      reminderFrequency: 'daily'
    }
  };

  if (assistantType === 'earne') {
    baseProfile.personality = {
      enthusiasm: 0.8,
      helpfulness: 0.9,
      playfulness: 0.7,
      patience: 0.9
    };
  } else if (assistantType === 'burt') {
    baseProfile.personality = {
      professionalism: 0.9,
      helpfulness: 0.8,
      analytical: 0.9,
      efficiency: 0.8
    };
  }

  return baseProfile;
}

// ===== RECIPE INTEGRATION SYSTEM =====

// Popular family recipes database
const recipeDatabase = {
  'chicken noodle soup': {
    name: 'Chicken Noodle Soup',
    prepTime: '20 minutes',
    cookTime: '30 minutes',
    servings: 6,
    difficulty: 'Easy',
    ingredients: [
      { name: 'Chicken breast', amount: '2', unit: 'pounds', category: 'Meat & Seafood' },
      { name: 'Egg noodles', amount: '8', unit: 'oz', category: 'Pantry' },
      { name: 'Carrots', amount: '3', unit: 'medium', category: 'Produce' },
      { name: 'Celery', amount: '3', unit: 'stalks', category: 'Produce' },
      { name: 'Onion', amount: '1', unit: 'medium', category: 'Produce' },
      { name: 'Chicken broth', amount: '6', unit: 'cups', category: 'Pantry' },
      { name: 'Salt', amount: '1', unit: 'tsp', category: 'Pantry' },
      { name: 'Black pepper', amount: '1/2', unit: 'tsp', category: 'Pantry' },
      { name: 'Thyme', amount: '1', unit: 'tsp', category: 'Pantry' }
    ],
    instructions: [
      'Heat oil in a large pot over medium heat',
      'Add diced onion, carrots, and celery. Cook 5 minutes',
      'Add chicken broth and bring to a boil',
      'Add diced chicken breast and simmer 15 minutes',
      'Add noodles and cook according to package directions',
      'Season with salt, pepper, and thyme',
      'Serve hot'
    ],
    cookingTips: [
      'Check chicken is cooked through - internal temperature 165°F',
      'Don\'t overcook noodles - they should be al dente',
      'Taste and adjust seasoning before serving'
    ]
  },
  'spaghetti and meatballs': {
    name: 'Spaghetti and Meatballs',
    prepTime: '30 minutes',
    cookTime: '45 minutes',
    servings: 4,
    difficulty: 'Medium',
    ingredients: [
      { name: 'Ground beef', amount: '1', unit: 'lb', category: 'Meat & Seafood' },
      { name: 'Spaghetti noodles', amount: '1', unit: 'lb', category: 'Pantry' },
      { name: 'Marinara sauce', amount: '24', unit: 'oz', category: 'Pantry' },
      { name: 'Breadcrumbs', amount: '1/2', unit: 'cup', category: 'Pantry' },
      { name: 'Egg', amount: '1', unit: 'large', category: 'Dairy' },
      { name: 'Parmesan cheese', amount: '1/4', unit: 'cup', category: 'Dairy' },
      { name: 'Garlic', amount: '3', unit: 'cloves', category: 'Produce' },
      { name: 'Olive oil', amount: '2', unit: 'tbsp', category: 'Pantry' },
      { name: 'Salt and pepper', amount: 'to taste', unit: '', category: 'Pantry' }
    ],
    instructions: [
      'Mix ground beef, breadcrumbs, egg, parmesan, and minced garlic',
      'Form into 1-inch meatballs',
      'Heat oil in large pan and brown meatballs on all sides',
      'Add marinara sauce and simmer 20 minutes',
      'Cook spaghetti according to package directions',
      'Serve meatballs and sauce over pasta'
    ],
    cookingTips: [
      'Don\'t overmix meatball mixture - handle gently',
      'Brown meatballs well for better flavor',
      'Test pasta doneness by tasting - should be al dente'
    ]
  },
  'tacos': {
    name: 'Ground Beef Tacos',
    prepTime: '15 minutes',
    cookTime: '20 minutes',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      { name: 'Ground beef', amount: '1', unit: 'lb', category: 'Meat & Seafood' },
      { name: 'Taco shells', amount: '12', unit: 'count', category: 'Pantry' },
      { name: 'Lettuce', amount: '1', unit: 'head', category: 'Produce' },
      { name: 'Tomatoes', amount: '2', unit: 'medium', category: 'Produce' },
      { name: 'Cheddar cheese', amount: '1', unit: 'cup', category: 'Dairy' },
      { name: 'Sour cream', amount: '1/2', unit: 'cup', category: 'Dairy' },
      { name: 'Taco seasoning', amount: '1', unit: 'packet', category: 'Pantry' },
      { name: 'Onion', amount: '1', unit: 'medium', category: 'Produce' }
    ],
    instructions: [
      'Brown ground beef in large skillet',
      'Add taco seasoning and water, simmer 5 minutes',
      'Warm taco shells according to package directions',
      'Dice tomatoes, lettuce, and onion',
      'Fill shells with meat mixture',
      'Top with cheese, lettuce, tomatoes, and sour cream'
    ],
    cookingTips: [
      'Drain excess fat from beef before adding seasoning',
      'Warm taco shells for 30 seconds to prevent breaking',
      'Let everyone build their own tacos for fun!'
    ]
  },
  'pancakes': {
    name: 'Fluffy Pancakes',
    prepTime: '10 minutes',
    cookTime: '15 minutes',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      { name: 'All-purpose flour', amount: '2', unit: 'cups', category: 'Pantry' },
      { name: 'Sugar', amount: '2', unit: 'tbsp', category: 'Pantry' },
      { name: 'Baking powder', amount: '2', unit: 'tsp', category: 'Pantry' },
      { name: 'Salt', amount: '1', unit: 'tsp', category: 'Pantry' },
      { name: 'Milk', amount: '1 1/2', unit: 'cups', category: 'Dairy' },
      { name: 'Egg', amount: '1', unit: 'large', category: 'Dairy' },
      { name: 'Butter', amount: '3', unit: 'tbsp', category: 'Dairy' },
      { name: 'Vanilla extract', amount: '1', unit: 'tsp', category: 'Pantry' }
    ],
    instructions: [
      'Mix dry ingredients in large bowl',
      'Whisk wet ingredients in separate bowl',
      'Combine wet and dry ingredients until just mixed',
      'Heat griddle or pan over medium heat',
      'Pour 1/4 cup batter for each pancake',
      'Flip when bubbles form and edges look set',
      'Cook until golden brown'
    ],
    cookingTips: [
      'Don\'t overmix batter - lumps are okay',
      'Cook on medium heat to prevent burning',
      'Flip pancakes only once for best texture'
    ]
  },
  'mac and cheese': {
    name: 'Homemade Mac and Cheese',
    prepTime: '15 minutes',
    cookTime: '25 minutes',
    servings: 6,
    difficulty: 'Medium',
    ingredients: [
      { name: 'Macaroni pasta', amount: '1', unit: 'lb', category: 'Pantry' },
      { name: 'Cheddar cheese', amount: '3', unit: 'cups', category: 'Dairy' },
      { name: 'Milk', amount: '2', unit: 'cups', category: 'Dairy' },
      { name: 'Butter', amount: '4', unit: 'tbsp', category: 'Dairy' },
      { name: 'Flour', amount: '1/4', unit: 'cup', category: 'Pantry' },
      { name: 'Salt', amount: '1/2', unit: 'tsp', category: 'Pantry' },
      { name: 'Black pepper', amount: '1/4', unit: 'tsp', category: 'Pantry' },
      { name: 'Paprika', amount: '1/4', unit: 'tsp', category: 'Pantry' }
    ],
    instructions: [
      'Cook macaroni according to package directions',
      'Melt butter in large saucepan',
      'Whisk in flour and cook 1 minute',
      'Gradually whisk in milk and bring to simmer',
      'Add cheese gradually while stirring',
      'Season with salt, pepper, and paprika',
      'Mix with cooked pasta and serve'
    ],
    cookingTips: [
      'Shred cheese yourself for better melting',
      'Add cheese gradually to prevent clumping',
      'Don\'t let sauce boil after adding cheese'
    ]
  }
};

/**
 * Search for recipes by name or keyword (checks family recipes first, then built-in)
 * @param {string} searchTerm - Recipe name or keyword
 * @param {string} familyId - Family ID to search family recipes
 * @returns {Promise<object|null>} Recipe object or null if not found
 */
export async function findRecipe(searchTerm, familyId = null) {
  const term = searchTerm.toLowerCase().trim();

  // First, try to find in family recipes if familyId provided
  if (familyId) {
    try {
      const recipesRef = collection(db, 'familyRecipes');
      const q = query(
        recipesRef,
        where('familyId', '==', familyId),
        where('name', '>=', term),
        where('name', '<=', term + '\uf8ff')
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const familyRecipe = snapshot.docs[0].data();
        return {
          ...familyRecipe,
          id: snapshot.docs[0].id,
          source: 'family'
        };
      }

      // Also try partial match in family recipes
      const allFamilyRecipes = query(recipesRef, where('familyId', '==', familyId));
      const allSnapshot = await getDocs(allFamilyRecipes);
      
      for (const doc of allSnapshot.docs) {
        const recipe = doc.data();
        if (recipe.name.toLowerCase().includes(term)) {
          return {
            ...recipe,
            id: doc.id,
            source: 'family'
          };
        }
      }
    } catch (error) {
      console.error('Error searching family recipes:', error);
    }
  }

  // Second, try built-in recipes
  if (recipeDatabase[term]) {
    return { ...recipeDatabase[term], source: 'built-in' };
  }

  // Partial match in built-in recipes
  for (const [key, recipe] of Object.entries(recipeDatabase)) {
    if (key.includes(term) || recipe.name.toLowerCase().includes(term)) {
      return { ...recipe, source: 'built-in' };
    }
  }

  // Third, search Spoonacular API for 360K+ recipes!
  try {
    console.log('🔍 Searching Spoonacular API for:', term);
    const apiRecipes = await searchRecipes(term, 5);
    console.log('📚 Spoonacular returned', apiRecipes?.length || 0, 'recipes');
    
    if (apiRecipes && apiRecipes.length > 0) {
      // Log what we found
      apiRecipes.forEach((r, i) => {
        console.log(`  ${i + 1}. ${r.title}`);
      });
      
      // Try to find a better match by looking for exact term matches
      let bestMatch = apiRecipes[0]; // Default to first result
      
      // Look for recipes that contain the search term more closely
      const searchWords = term.toLowerCase().split(' ');
      for (const recipe of apiRecipes) {
        const title = recipe.title.toLowerCase();
        const matchScore = searchWords.reduce((score, word) => {
          if (title.includes(word)) score++;
          return score;
        }, 0);
        
        // If this recipe has a higher match score, use it
        if (matchScore > searchWords.length * 0.5) {
          bestMatch = recipe;
          console.log(`🎯 Better match found: ${recipe.title} (score: ${matchScore}/${searchWords.length})`);
          break;
        }
      }
      
      console.log('🍽️ Getting details for:', bestMatch.title);
      const details = await getRecipeDetails(bestMatch.id);
      
      if (details) {
        const formatted = formatRecipeForApp(details);
        console.log('✅ Recipe formatted successfully');
        return {
          ...formatted,
          source: 'spoonacular',
          servings: formatted.servings,
          readyInMinutes: formatted.readyInMinutes,
          // Store all search results for user to choose from
          alternativeRecipes: apiRecipes.slice(1, 5).map(r => ({
            id: r.id,
            title: r.title,
            image: r.image
          }))
        };
      }
    }
  } catch (error) {
    console.error('❌ Error searching Spoonacular API:', error);
  }

  console.log('❌ No recipe found for:', term);
  return null;
}

/**
 * Get all available recipes
 * @returns {Array} Array of recipe names
 */
export function getAllRecipes() {
  return Object.keys(recipeDatabase).map(key => recipeDatabase[key].name);
}

/**
 * Add recipe ingredients to shopping list
 * @param {object} recipe - Recipe object
 * @param {function} addShoppingItem - Function to add items to shopping list
 * @param {string} requestedBy - Who requested the recipe
 */
export async function addRecipeToShoppingList(recipe, addShoppingItem, requestedBy) {
  const addedItems = [];
  
  for (const ingredient of recipe.ingredients) {
    const itemName = `${ingredient.amount} ${ingredient.unit} ${ingredient.name}`.trim();
    
    try {
      await addShoppingItem({
        name: itemName,
        price: estimateIngredientPrice(ingredient),
        category: ingredient.category,
        requestedBy: requestedBy,
        requestedByUid: requestedBy,
        recipeSource: recipe.name,
        store: 'amazon' // Default to amazon
      });
      addedItems.push(itemName);
    } catch (error) {
      console.error(`Error adding ingredient ${itemName}:`, error);
    }
  }
  
  return addedItems;
}

/**
 * Estimate price for an ingredient
 * @param {object} ingredient - Ingredient object
 * @returns {number} Estimated price
 */
function estimateIngredientPrice(ingredient) {
  const basePrices = {
    'Meat & Seafood': { 'chicken': 3.50, 'beef': 4.00, 'pork': 3.00 },
    'Produce': { 'carrot': 0.50, 'onion': 0.75, 'tomato': 1.00, 'lettuce': 1.50 },
    'Dairy': { 'milk': 2.50, 'cheese': 3.00, 'butter': 2.00, 'egg': 0.25 },
    'Pantry': { 'pasta': 1.50, 'sauce': 2.00, 'seasoning': 1.00, 'flour': 1.00 }
  };
  
  const category = ingredient.category;
  const name = ingredient.name.toLowerCase();
  
  if (basePrices[category]) {
    for (const [key, price] of Object.entries(basePrices[category])) {
      if (name.includes(key)) {
        // Adjust for quantity
        const quantity = parseFloat(ingredient.amount) || 1;
        return price * Math.max(quantity, 1);
      }
    }
  }
  
  // Default pricing based on category
  const defaultPrices = {
    'Meat & Seafood': 3.50,
    'Produce': 1.00,
    'Dairy': 2.50,
    'Pantry': 1.50,
    'Bakery': 2.00,
    'Frozen': 2.50
  };
  
  return defaultPrices[category] || 2.00;
}

/**
 * Get cooking guidance for a recipe step
 * @param {string} recipeName - Name of the recipe
 * @param {number} stepNumber - Step number (1-based)
 * @returns {object} Cooking guidance information
 */
export function getCookingGuidance(recipeName, stepNumber) {
  const recipe = findRecipe(recipeName);
  if (!recipe || !recipe.instructions[stepNumber - 1]) {
    return null;
  }
  
  const instruction = recipe.instructions[stepNumber - 1];
  const guidance = {
    step: stepNumber,
    instruction: instruction,
    tips: [],
    timers: [],
    measurements: []
  };
  
  // Extract timing information
  const timeMatch = instruction.match(/(\d+)\s*(minute|min|hour|hr)/gi);
  if (timeMatch) {
    timeMatch.forEach(match => {
      const num = match.match(/\d+/)[0];
      const unit = match.match(/(minute|min|hour|hr)/i)[0];
      guidance.timers.push({
        duration: parseInt(num),
        unit: unit.toLowerCase(),
        description: `${num} ${unit}`
      });
    });
  }
  
  // Extract measurement information
  const measureMatch = instruction.match(/(\d+(?:\/\d+)?)\s*(cup|cups|tbsp|tsp|oz|pound|lb)/gi);
  if (measureMatch) {
    measureMatch.forEach(match => {
      guidance.measurements.push({
        amount: match.match(/(\d+(?:\/\d+)?)/)[0],
        unit: match.match(/(cup|cups|tbsp|tsp|oz|pound|lb)/i)[0],
        full: match
      });
    });
  }
  
  // Add relevant cooking tips
  if (recipe.cookingTips) {
    guidance.tips = recipe.cookingTips.filter(tip => 
      tip.toLowerCase().includes(instruction.toLowerCase().split(' ')[0])
    );
  }
  
  return guidance;
}

/**
 * Get doneness check instructions for cooking
 * @param {string} item - What's being cooked (chicken, pasta, etc.)
 * @returns {object} Doneness check information
 */
export function getDonenessCheck(item) {
  const checks = {
    'chicken': {
      temperature: '165°F (74°C)',
      visual: 'No pink in center, juices run clear',
      touch: 'Firm to touch, springs back slightly',
      timing: '15-20 minutes per pound'
    },
    'pasta': {
      temperature: 'N/A',
      visual: 'Translucent, slightly firm',
      touch: 'Al dente - firm but not hard',
      timing: '8-12 minutes depending on type'
    },
    'beef': {
      temperature: '145°F (63°C) for medium',
      visual: 'Brown on outside, pink in center',
      touch: 'Firm but gives slightly',
      timing: '3-4 minutes per side'
    },
    'eggs': {
      temperature: 'N/A',
      visual: 'Whites set, yolk to preference',
      touch: 'Firm whites, soft yolk',
      timing: '3-4 minutes for over easy'
    }
  };
  
  const itemLower = item.toLowerCase();
  for (const [key, check] of Object.entries(checks)) {
    if (itemLower.includes(key)) {
      return check;
    }
  }
  
  return {
    temperature: 'Check recipe',
    visual: 'Cook until done',
    touch: 'Use your judgment',
    timing: 'Follow recipe timing'
  };
}

/**
 * Convert measurements between units
 * @param {number} amount - Amount to convert
 * @param {string} fromUnit - Unit to convert from
 * @param {string} toUnit - Unit to convert to
 * @returns {object} Conversion result
 */
export function convertMeasurement(amount, fromUnit, toUnit) {
  const conversions = {
    'cup': {
      'tbsp': amount * 16,
      'tsp': amount * 48,
      'ml': amount * 236.6,
      'oz': amount * 8
    },
    'tbsp': {
      'cup': amount / 16,
      'tsp': amount * 3,
      'ml': amount * 14.8,
      'oz': amount / 2
    },
    'tsp': {
      'cup': amount / 48,
      'tbsp': amount / 3,
      'ml': amount * 4.9,
      'oz': amount / 6
    },
    'pound': {
      'oz': amount * 16,
      'kg': amount * 0.453,
      'g': amount * 453.6
    },
    'oz': {
      'pound': amount / 16,
      'kg': amount * 0.028,
      'g': amount * 28.35
    }
  };
  
  if (conversions[fromUnit] && conversions[fromUnit][toUnit]) {
    return {
      original: `${amount} ${fromUnit}`,
      converted: `${conversions[fromUnit][toUnit]} ${toUnit}`,
      amount: conversions[fromUnit][toUnit],
      unit: toUnit
    };
  }
  
  return null;
}

/**
 * Learn from user interaction and update AI profile
 * @param {string} userId - User's UID
 * @param {string} assistantType - 'earne' or 'burt'
 * @param {string} userMessage - User's message
 * @param {string} assistantResponse - Assistant's response
 * @param {object} context - Interaction context
 */
export async function learnFromInteraction(userId, assistantType, userMessage, assistantResponse, context = {}) {
  try {
    // Save the conversation
    await saveConversationMessage(userId, assistantType, 'user', userMessage, context);
    await saveConversationMessage(userId, assistantType, 'assistant', assistantResponse, context);

    // Get current profile
    const currentProfile = await getAIProfile(userId, assistantType);
    
    // Get recent conversation history
    const recentHistory = await getConversationHistory(userId, assistantType, 20);
    
    // Analyze patterns
    const newPatterns = analyzeConversationPatterns(recentHistory, assistantType);
    
    // Update profile with learned patterns
    const updatedProfile = {
      ...currentProfile,
      learnedPatterns: {
        ...currentProfile.learnedPatterns,
        ...newPatterns
      },
      lastInteraction: new Date().toISOString()
    };

    await updateAIProfile(userId, assistantType, updatedProfile);
    
    return updatedProfile;
  } catch (error) {
    console.error('Error learning from interaction:', error);
  }
}
